if (!customElements.get('recipient-form')) {
    customElements.define(
        'recipient-form',
        class RecipientForm extends HTMLElement {
            constructor() {
                super();

                const id = this.dataset.sectionId;

                this.checkbox = this.querySelector(`#Recipient-checkbox-${id}`);
                this.hiddenControl = this.querySelector(`#Recipient-control-${id}`);
                this.offset = this.querySelector(`#Recipient-timezone-offset-${id}`);
                this.fieldsWrapper = this.querySelector('.recipient-fields');

                this.emailInput = this.querySelector(`#Recipient-email-${id}`);
                this.nameInput = this.querySelector(`#Recipient-name-${id}`);
                this.messageInput = this.querySelector(`#Recipient-message-${id}`);
                this.sendOnInput = this.querySelector(`#Recipient-send-on-${id}`);

                this.emailError = this.querySelector(`#RecipientForm-email-error-${id}, #Recipient-email-error-${id}`);
                this.nameError = this.querySelector(`#RecipientForm-name-error-${id}, #Recipient-name-error-${id}`);
                this.messageError = this.querySelector(`#RecipientForm-message-error-${id}, #Recipient-message-error-${id}`);
                this.sendOnError = this.querySelector(`#RecipientForm-send_on-error-${id}, #Recipient-send-on-error-${id}`);

                this.fields = [
                    this.emailInput,
                    this.nameInput,
                    this.messageInput,
                    this.sendOnInput,
                ].filter(Boolean);

                this.messageCounter = this.querySelector('.js-recipient-message-count');

                this.handleDocumentChange = this.handleDocumentChange.bind(this);
                this.handleSubmit = this.handleSubmit.bind(this);
                this.handleChange = this.handleChange.bind(this);

                if (this.checkbox) this.checkbox.disabled = false;
                if (this.hiddenControl) this.hiddenControl.disabled = true;
                if (this.offset) this.offset.value = String(new Date().getTimezoneOffset());

                if (this.sendOnInput) {
                    const today = new Date();
                    const yyyy = today.getFullYear();
                    const mm = String(today.getMonth() + 1).padStart(2, '0');
                    const dd = String(today.getDate()).padStart(2, '0');
                    this.sendOnInput.min = `${yyyy}-${mm}-${dd}`;
                }

                this.addEventListener('change', this.handleChange);
                document.addEventListener('change', this.handleDocumentChange);

                if (this.messageInput) {
                    this.messageInput.addEventListener('input', () => {
                        this.updateCharCount();
                        this.clearFieldError(this.messageInput, this.messageError);
                    });
                }

                if (this.emailInput) {
                    this.emailInput.addEventListener('input', () => {
                        this.clearFieldError(this.emailInput, this.emailError);
                        this.updateSubmitButtonState();
                    });

                    this.emailInput.addEventListener('blur', () => {
                        this.updateSubmitButtonState();
                    });
                }

                if (this.nameInput) {
                    this.nameInput.addEventListener('input', () => {
                        this.clearFieldError(this.nameInput, this.nameError);
                    });
                }

                if (this.sendOnInput) {
                    this.sendOnInput.addEventListener('input', () => {
                        this.clearFieldError(this.sendOnInput, this.sendOnError);
                    });

                    this.sendOnInput.addEventListener('change', () => {
                        this.clearFieldError(this.sendOnInput, this.sendOnError);
                    });
                }

                const form = this.getAssociatedForm();
                if (form) {
                    form.addEventListener('submit', this.handleSubmit);
                }

                this.update();
                this.updateCharCount();
                this.updateSubmitButtonState();
            }

            disconnectedCallback() {
                document.removeEventListener('change', this.handleDocumentChange);

                const form = this.getAssociatedForm();
                if (form) {
                    form.removeEventListener('submit', this.handleSubmit);
                }
            }

            getAssociatedForm() {
                const formId =
                    this.checkbox?.getAttribute('form') ||
                    this.emailInput?.getAttribute('form') ||
                    this.messageInput?.getAttribute('form');

                if (formId) return document.getElementById(formId);
                return this.closest('form');
            }

            getSubmitButton() {
                const form = this.getAssociatedForm();
                if (!form) return null;

                return form.querySelector('button[type="submit"][name="add"], button[type="submit"]');
            }

            getToggleableFields() {
                return [...this.fields, this.offset].filter(Boolean);
            }

            enableFields() {
                this.getToggleableFields().forEach((field) => {
                    field.disabled = false;
                });
            }

            disableFields() {
                this.getToggleableFields().forEach((field) => {
                    field.disabled = true;
                });
            }

            clearFields() {
                this.fields.forEach((field) => {
                    field.value = '';
                });
            }

            handleChange(event) {
                if (event.target === this.checkbox) {
                    this.update();
                }
            }

            handleDocumentChange(event) {
                const target = event.target;
                if (!(target instanceof HTMLInputElement)) return;

                const formId = this.checkbox?.getAttribute('form');
                if (formId && target.type === 'radio' && target.getAttribute('form') === formId) {
                    window.setTimeout(() => {
                        this.clearErrors();
                        this.updateSubmitButtonState();
                    }, 0);
                }
            }

            update() {
                if (!this.checkbox) return;

                const enabled = this.checkbox.checked;

                if (this.fieldsWrapper) {
                    this.fieldsWrapper.classList.toggle('hidden', !enabled);
                }

                if (enabled) {
                    this.enableFields();
                } else {
                    this.clearErrors();
                    this.clearFields();
                    this.disableFields();
                }

                this.updateCharCount();
                this.updateSubmitButtonState();
            }

            updateCharCount() {
                if (!this.messageInput || !this.messageCounter) return;

                const max = Number(this.messageInput.getAttribute('maxlength')) || 200;
                const currentLength = this.messageInput.value.length;
                const remaining = Math.max(0, max - currentLength);

                this.messageCounter.textContent = `${remaining} characters remaining`;
            }

            applyRecipientButtonLock(shouldLock) {
                const submitButton = this.getSubmitButton();
                if (!submitButton) return;

                if (shouldLock) {
                    submitButton.dataset.recipientDisabled = 'true';
                    submitButton.classList.add('disabled');
                    submitButton.setAttribute('aria-disabled', 'true');

                    if (!submitButton.disabled) {
                        submitButton.dataset.recipientDisabledByScript = 'true';
                    }

                    submitButton.disabled = true;
                    return;
                }

                delete submitButton.dataset.recipientDisabled;
                submitButton.classList.remove('disabled');
                submitButton.removeAttribute('aria-disabled');

                if (submitButton.dataset.recipientDisabledByScript === 'true') {
                    submitButton.disabled = false;
                    delete submitButton.dataset.recipientDisabledByScript;
                }
            }

            updateSubmitButtonState() {
                if (!this.checkbox?.checked) {
                    this.applyRecipientButtonLock(false);
                    return;
                }

                const email = this.emailInput?.value.trim() || '';
                const hasValidEmail = email !== '' && this.isValidEmail(email);

                this.applyRecipientButtonLock(!hasValidEmail);
            }

            handleSubmit(event) {
                if (!this.checkbox?.checked) {
                    this.applyRecipientButtonLock(false);
                    return;
                }

                const isValid = this.validate();

                if (!isValid) {
                    event.preventDefault();

                    const firstInvalidField = [
                        this.emailInput,
                        this.nameInput,
                        this.messageInput,
                        this.sendOnInput,
                    ].find((input) => input?.getAttribute('aria-invalid') === 'true');

                    if (firstInvalidField) firstInvalidField.focus();
                    return;
                }

                this.applyRecipientButtonLock(false);
            }

            validate() {
                this.clearErrors();

                let isValid = true;

                const email = this.emailInput?.value.trim() || '';
                const message = this.messageInput?.value || '';
                const sendOn = this.sendOnInput?.value.trim() || '';

                if (!email) {
                    this.setFieldError(this.emailInput, this.emailError, 'Enter a recipient email.');
                    isValid = false;
                } else if (!this.isValidEmail(email)) {
                    this.setFieldError(this.emailInput, this.emailError, 'Enter a valid email address.');
                    isValid = false;
                }

                const maxMessageLength = Number(this.messageInput?.getAttribute('maxlength')) || 200;
                if (message.length > maxMessageLength) {
                    this.setFieldError(
                        this.messageInput,
                        this.messageError,
                        `Message must be ${maxMessageLength} characters or fewer.`
                    );
                    isValid = false;
                }

                if (sendOn && !this.isValidFutureOrTodayDate(sendOn)) {
                    this.setFieldError(this.sendOnInput, this.sendOnError, 'Choose today or a future date.');
                    isValid = false;
                }

                this.updateSubmitButtonState();
                return isValid;
            }

            isValidEmail(value) {
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            }

            isValidFutureOrTodayDate(value) {
                const selectedDate = new Date(`${value}T00:00:00`);
                if (Number.isNaN(selectedDate.getTime())) return false;

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                return selectedDate >= today;
            }

            getFieldWrapper(input) {
                return input?.closest('.field');
            }

            setFieldError(input, errorElement, message) {
                if (!input || !errorElement) return;

                const wrapper = this.getFieldWrapper(input);

                input.setAttribute('aria-invalid', 'true');
                input.setAttribute('aria-describedby', errorElement.id);

                if (wrapper) {
                    wrapper.classList.add('field--with-error');
                }

                errorElement.textContent = message;
                errorElement.classList.remove('hidden');
            }

            clearFieldError(input, errorElement) {
                if (input) {
                    input.removeAttribute('aria-invalid');
                    input.removeAttribute('aria-describedby');

                    const wrapper = this.getFieldWrapper(input);
                    if (wrapper) {
                        wrapper.classList.remove('field--with-error');
                    }
                }

                if (errorElement) {
                    errorElement.textContent = '';
                    errorElement.classList.add('hidden');
                }
            }

            clearErrors() {
                this.clearFieldError(this.emailInput, this.emailError);
                this.clearFieldError(this.nameInput, this.nameError);
                this.clearFieldError(this.messageInput, this.messageError);
                this.clearFieldError(this.sendOnInput, this.sendOnError);
            }
        }
    );
}